/**
 * What a sync writes to a connected store, field by field, read from the push code.
 *
 * This is the data behind "What Motzu syncs" on an integration's Sync behavior tab and the
 * Help Center article of the same name. It describes behaviour that exists; it changes none.
 * The engineering version, with a file:line for every cell, is docs/internal/SYNC_BEHAVIOR_MATRIX.md,
 * and `src/__tests__/syncBehaviorMatrix.test.ts` pins key cells against the real push code so
 * the copy cannot drift from what the sync does. `refs` name the function that decides each cell
 * (no line numbers here: they move with every edit; the doc carries them).
 *
 * No React and no imports at all, so the API test suite and type check can include it.
 */
// Mirrors of the integration settings types (`services/integrationApi.ts`), kept local so this file
// imports nothing and the API's type check does not pull the frontend API client in with it.
export type SyncMode = 'link_only' | 'enrich' | 'override';
export type OverrideField = 'title' | 'description' | 'images' | 'tags' | 'vendorStatus' | 'productType' | 'weight';

/** The integration settings this module reads; `IntegrationSettings` satisfies it. */
export interface SyncBehaviorSettings {
  syncMode?: SyncMode;
  overrideFields?: Partial<Record<OverrideField, boolean>>;
  syncPricing?: boolean;
  syncInventory?: boolean;
  syncImages?: boolean;
  syncProducts?: boolean;
  /** Starting stock count for a product Motzu creates. */
  defaultInventoryCount?: number;
  /** Shopify (`initial_sync_mode`): what a selected product with no listing gets. */
  unmatchedProductAction?: 'create' | 'skip';
  /** Shopify (`shopify_listing_shape`): what happens to a listing Motzu created when its product leaves the sync. */
  onUnsync?: 'leave' | 'draft' | 'archive';
  /** Shopify (`shopify_respect_store_deletes`): a listing deleted in Shopify is created again. */
  recreateDeletedProducts?: boolean;
  /** Shopify (`shopify_metafield_switches`): `false` turns one Motzu metafield off. */
  motzuMetafields?: Record<string, boolean>;
  /** Lightspeed Retail link only: reprice linked items. */
  linkOnlyPricing?: boolean;
  /** Lightspeed eCom: which content fields the push writes. Missing key = the default. */
  lightspeedEcom?: Partial<Record<LightspeedEcomPushKey, boolean>>;
  /** Shopify (`shopify_tag_mode`): `marker_only` leaves every tag but the marker and lifecycle ones to the store. */
  tagMode?: 'motzu' | 'marker_only';
}

export type LightspeedEcomPushKey = 'content' | 'description' | 'tags' | 'metafields' | 'visibility';

/** Mirrors `LIGHTSPEED_ECOM_PUSH_DEFAULTS` (`src/types/lightspeedEcom.ts`); the API test pins the two together. */
export const LIGHTSPEED_ECOM_PUSH_DEFAULTS_MIRROR: Readonly<Record<LightspeedEcomPushKey, boolean>> = {
  content: true,
  description: true,
  tags: true,
  metafields: true,
  visibility: false,
};

export type SyncBehaviorPlatform = 'shopify' | 'lightspeed' | 'lightspeed_ecom' | 'bigcommerce' | 'woocommerce';

export const SYNC_BEHAVIOR_FIELDS = [
  'title',
  'description',
  'images',
  'price',
  'instantRebate',
  'cost',
  'inventory',
  'barcode',
  'sku',
  'weight',
  'tags',
  'productType',
  'vendor',
  'collections',
  'metafields',
  'seoTitle',
  'seoDescription',
  'status',
] as const;

export type SyncBehaviorField = (typeof SYNC_BEHAVIOR_FIELDS)[number];

/**
 * What one sync does to a field on a listing that is already linked.
 * - `replace`: Motzu's value replaces the store's.
 * - `fill_blank`: written only while the store's field is empty.
 * - `add`: Motzu's entries are added and the store's stay (tags, photos).
 * - `price_runs`: a full sync never writes it; the scheduled price updates replace it.
 * - `untouched`: never sent.
 */
export type SyncFieldWrite = 'replace' | 'fill_blank' | 'add' | 'price_runs' | 'untouched';

/**
 * How a product Motzu creates gets this field: always (`true`), never (`false`), or only when a
 * saved switch allows it (`syncImages`, `syncPricing`, or `startingCount` for a starting stock count).
 */
export type SyncCreateRule = boolean | 'syncImages' | 'syncPricing' | 'startingCount';

/** The Data sync switches that turn a field off in every mode. */
export type SyncDataToggle = 'syncPricing' | 'syncInventory' | 'syncImages' | 'syncProducts';

export interface SyncFieldCell {
  field: SyncBehaviorField;
  /** Written on a product Motzu creates; see `createOutcome`. */
  onCreate: SyncCreateRule;
  modes: Record<SyncMode, SyncFieldWrite>;
  /** The "What Override replaces" row that keeps the store's value in Override. */
  overrideField?: OverrideField;
  /** The Data sync switch that stops the write in every mode. */
  toggle?: SyncDataToggle;
  /** A Data sync switch that stops the write in one mode only. */
  modeToggles?: Partial<Record<SyncMode, SyncDataToggle>>;
  /** Lightspeed eCom: the "what to push" switch for this field. */
  ecomSettings?: LightspeedEcomPushKey[];
  /** Shopify (`shopify_metafield_switches`): the Motzu metafield switch for this field. */
  motzuMetafieldKey?: string;
  /**
   * For a `replace` write, when Motzu no longer has a value (a spec, a barcode, a photo set, a sale
   * ends): `cleared` from the store, or `left` there as last written. A `fill_blank` or `add`
   * write always leaves what is there, unless `addReconciles` says otherwise.
   */
  whenRemoved: 'cleared' | 'left';
  /**
   * For an `add` write: Motzu remembers what it added and takes back an entry it no longer wants
   * (only Lightspeed eCom tags today). Missing = additive only, nothing is ever removed.
   */
  addReconciles?: boolean;
  /**
   * Shopify, flag `shopify_owned_field_cleanup` (off by default): in these modes Motzu remembers what it
   * wrote and takes back an entry it added and no longer sends (`reconcilesOwned`). With the flag off
   * the cell's own `whenRemoved` / `addReconciles` stand.
   */
  ownedCleanup?: SyncMode[];
  /** Note shown instead of `noteKey` while `ownedCleanup` applies. */
  ownedCleanupNoteKey?: string;
  /**
   * Lightspeed Retail with `lightspeed_catalog_parity` off: what an update writes instead of the
   * mode's cell (the full item is sent whatever the mode).
   */
  withoutParity?: SyncFieldWrite;
  /** Flag `enrich_fill_only` on: what Fill gaps writes instead of `modes.enrich` (only ever `fill_blank`). */
  enrichFillOnly?: SyncFieldWrite;
  /** Extra line under the field, `integrations.whatMotzuSyncs.notes.<noteKey>`. */
  noteKey?: string;
  /** The note to show instead of `noteKey` while flag `enrich_fill_only` is on (`fieldNoteKey`). */
  fillOnlyNoteKey?: string;
  /** The note to show instead of `noteKey` while flag `lightspeed_photo_topup` is on (`fieldNoteKey`). */
  photoTopUpNoteKey?: string;
  refs: string[];
}

/** The things a store does that a customer expects Motzu to react to. */
export const SYNC_BEHAVIOR_EVENTS = ['deselect', 'modeChange', 'overrideOff', 'disconnect', 'scheduled', 'deletedInStore'] as const;
export type SyncBehaviorEvent = (typeof SYNC_BEHAVIOR_EVENTS)[number];

export interface SyncEventCell {
  event: SyncBehaviorEvent;
  /** `integrations.whatMotzuSyncs.events.<outcome>` */
  outcome: string;
  refs: string[];
}

export interface SyncPlatformBehavior {
  platform: SyncBehaviorPlatform;
  /** Whether the integration's sync mode changes what is written. */
  honoursSyncMode: boolean;
  /** Whether the "What Override replaces" choices apply. */
  honoursOverrideFields: boolean;
  /** No sync runs for this platform yet (BigCommerce, WooCommerce until the MOT-283 rebuild). */
  syncPaused?: boolean;
  fields: SyncFieldCell[];
  events: SyncEventCell[];
}

const SHOPIFY = 'src/services/shopifyService.ts';
const LIGHTSPEED = 'src/services/lightspeedService.ts';
const PARITY = 'src/utils/lightspeedCatalogParity.ts';
const LEGACY = 'src/services/integrationService.ts';

const all = (write: SyncFieldWrite): Record<SyncMode, SyncFieldWrite> => ({
  link_only: write,
  enrich: write,
  override: write,
});

const SHOPIFY_FIELDS: SyncFieldCell[] = [
  {
    field: 'title',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'title',
    // The Products switch stops every listing field in Override (`overrideFieldsForProductsSwitch`).
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    refs: [`${SHOPIFY}#updateShopifyProduct`, `${SHOPIFY}#enrichShopifyProduct`],
  },
  {
    field: 'description',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'description',
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'cleared',
    noteKey: 'shopifyDescription',
    refs: [`${SHOPIFY}#updateShopifyProduct`, `${SHOPIFY}#buildDescriptionWithSpecs`],
  },
  {
    field: 'images',
    onCreate: 'syncImages',
    modes: { link_only: 'untouched', enrich: 'add', override: 'replace' },
    overrideField: 'images',
    toggle: 'syncImages',
    // Enrich still appends photos with the Products switch off; Override leaves them.
    modeToggles: { override: 'syncProducts' },
    // With no Motzu photos the update carries no photo set, so the listing keeps its own.
    whenRemoved: 'left',
    noteKey: 'shopifyImages',
    refs: [`${SHOPIFY}#updateShopifyProduct`, `${SHOPIFY}#enrichShopifyProduct`],
  },
  {
    field: 'price',
    onCreate: 'syncPricing',
    // A full Enrich run never writes a variant (`pricePushed = outcome?.status !== 'enriched'`); only
    // the `pricing_only` runs (the scheduled price updates) reprice an enriched listing.
    modes: { link_only: 'replace', enrich: 'price_runs', override: 'replace' },
    toggle: 'syncPricing',
    whenRemoved: 'cleared',
    // Link only clears only a compare-at Motzu wrote (`shopifyCompareAtClearAllowed`); the note says so.
    noteKey: 'shopifyPrice',
    refs: [
      `${SHOPIFY}#updateShopifyProductPricing`,
      'src/utils/shopifyVariantSync.ts#shopifyVariantPriceWrite',
      'src/utils/shopifyVariantSync.ts#shopifyCompareAtClearAllowed',
      `${SHOPIFY}#const pricePushed`,
    ],
  },
  {
    field: 'instantRebate',
    onCreate: true,
    // Link only writes it with the price (`priceLinkOnlyListing`), Enrich and Override with the
    // metafield upsert, and every mode on the pricing-only runs. A rebate that ends is written as 0.
    modes: all('replace'),
    modeToggles: { link_only: 'syncPricing' },
    motzuMetafieldKey: 'instant_rebate',
    whenRemoved: 'cleared',
    noteKey: 'shopifyInstantRebate',
    refs: [`${SHOPIFY}#syncInstantRebateMetafield`, `${SHOPIFY}#resolveInstantRebateMetafieldValue`, `${SHOPIFY}#const priceLinkOnlyListing`],
  },
  {
    field: 'cost',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    refs: [`${SHOPIFY}#buildShopifyVariants`],
  },
  {
    field: 'inventory',
    // The starting count is set only on a product Motzu creates, never on an update.
    onCreate: 'startingCount',
    modes: all('untouched'),
    toggle: 'syncInventory',
    whenRemoved: 'left',
    noteKey: 'shopifyInventory',
    refs: [`${SHOPIFY}#setInventoryLevels`, `${SHOPIFY}#createShopifyProduct`, `${SHOPIFY}#seedInitialInventory`],
  },
  {
    field: 'barcode',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    whenRemoved: 'cleared',
    noteKey: 'shopifyBarcode',
    refs: ['src/utils/shopifyVariantSync.ts#shopifyBarcodePayload', `${SHOPIFY}#syncOneProductToShopifyInternal`],
  },
  {
    field: 'sku',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'fill_blank' },
    whenRemoved: 'left',
    refs: [`${SHOPIFY}#updateShopifyProduct`],
  },
  {
    field: 'weight',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'weight',
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    noteKey: 'shopifyWeight',
    refs: [`${SHOPIFY}#updateShopifyProduct`, `${SHOPIFY}#extractVariantWeight`],
  },
  {
    field: 'tags',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'add', override: 'add' },
    overrideField: 'tags',
    // Enrich still adds the motzu_synced marker with the Products switch off; Override sends no tags.
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    noteKey: 'shopifyTags',
    ownedCleanup: ['override'],
    ownedCleanupNoteKey: 'shopifyTagsOwnedCleanup',
    refs: [
      `${SHOPIFY}#mergeWithExistingShopifyTags`,
      `${SHOPIFY}#readShopifyListingTagsAndStatus`,
      `${SHOPIFY}#buildShopifyTaxonomy`,
      `${SHOPIFY}#enrichShopifyProduct`,
      `${SHOPIFY}#reconcileOwnedShopifyFields`,
      'src/utils/shopifyOwnedFields.ts#planOwnedTagCleanup',
    ],
  },
  {
    field: 'productType',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    overrideField: 'productType',
    // The Products switch stops type and category in every mode.
    toggle: 'syncProducts',
    whenRemoved: 'left',
    // Flag off, Fill gaps fills the type but a mapped category replaces the listing's; with
    // `enrich_fill_only` on both are set only when empty (the cell is `fill_blank` either way).
    noteKey: 'shopifyProductType',
    fillOnlyNoteKey: 'shopifyProductTypeFillOnly',
    refs: [
      `${SHOPIFY}#enrichShopifyProduct`,
      `${SHOPIFY}#applyShopifyProductCategory`,
      'src/utils/shopifyTaxonomyCategory.ts#shopifyFieldWriteDecision',
      'src/utils/shopifyTaxonomyCategory.ts#enrichFillOnlyOwner',
    ],
  },
  {
    field: 'vendor',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'vendorStatus',
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    refs: [`${SHOPIFY}#updateShopifyProduct`],
  },
  {
    field: 'collections',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    noteKey: 'shopifyCollections',
    refs: [`${SHOPIFY}#fetchSmartCollections`],
  },
  {
    field: 'metafields',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'replace', override: 'replace' },
    // The Products switch stops the spec fields in every mode.
    toggle: 'syncProducts',
    whenRemoved: 'left',
    noteKey: 'shopifyMetafields',
    ownedCleanup: ['enrich', 'override'],
    ownedCleanupNoteKey: 'shopifyMetafieldsOwnedCleanup',
    refs: [
      `${SHOPIFY}#upsertShopifyMetafields`,
      `${SHOPIFY}#metafieldsForSync`,
      `${SHOPIFY}#reconcileOwnedShopifyFields`,
      'src/utils/shopifyOwnedFields.ts#planOwnedMetafieldCleanup',
    ],
  },
  {
    field: 'seoTitle',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'title',
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    noteKey: 'shopifySeoTitle',
    refs: [`${SHOPIFY}#writeShopifySeo`],
  },
  {
    field: 'seoDescription',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'description',
    modeToggles: { override: 'syncProducts' },
    whenRemoved: 'left',
    noteKey: 'shopifySeoDescription',
    refs: [`${SHOPIFY}#writeShopifySeo`],
  },
  {
    field: 'status',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'vendorStatus',
    whenRemoved: 'left',
    // Override sets active only when Motzu set the current status (`lastPushed.listingStatus`); the
    // lifecycle's draft and archived are written. The Products switch does not stop it.
    noteKey: 'shopifyStatus',
    refs: [`${SHOPIFY}#getShopifyStatus`, `${SHOPIFY}#shopifyStatusToWrite`, `${SHOPIFY}#updateShopifyProduct`],
  },
];

/**
 * Lightspeed Retail (R-Series). The modes here are what the push does with
 * `lightspeed_catalog_parity` and `lightspeed_link_only` on; `resolveSyncMode` folds a mode the
 * store's flags do not support back to Override, which is what the push then runs.
 */
const LIGHTSPEED_FIELDS: SyncFieldCell[] = [
  {
    field: 'title',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    overrideField: 'title',
    whenRemoved: 'left',
    refs: [`${PARITY}#planLightspeedWrite`, 'src/services/lightspeed/payload/contributors/core.ts#coreContributor'],
  },
  {
    field: 'description',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'replace', override: 'replace' },
    // Flag `enrich_fill_only`: Fill gaps writes the web copy only to an item that has none.
    enrichFillOnly: 'fill_blank',
    overrideField: 'description',
    whenRemoved: 'left',
    noteKey: 'lightspeedDescription',
    fillOnlyNoteKey: 'lightspeedDescriptionFillOnly',
    refs: [
      'src/services/lightspeed/payload/contributors/description.ts#buildEcommerceCopy',
      `${PARITY}#planLightspeedWrite`,
      `${PARITY}#lightspeedWebCopy`,
    ],
  },
  {
    field: 'images',
    onCreate: 'syncImages',
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'fill_blank' },
    toggle: 'syncImages',
    whenRemoved: 'left',
    noteKey: 'lightspeedImages',
    photoTopUpNoteKey: 'lightspeedImagesTopUp',
    refs: [`${LIGHTSPEED}#postWriteHooks`],
  },
  {
    field: 'price',
    onCreate: 'syncPricing',
    modes: all('replace'),
    toggle: 'syncPricing',
    whenRemoved: 'cleared',
    noteKey: 'lightspeedPrice',
    refs: ['src/utils/erpStorefrontPricing.ts#toLightspeedItemPrices', `${PARITY}#lightspeedLinkOnlyPrices`],
  },
  {
    field: 'instantRebate',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    noteKey: 'lightspeedInstantRebate',
    refs: ['src/services/lightspeedSalesService.ts#syncRebateDiscounts'],
  },
  {
    field: 'cost',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    refs: ['src/services/lightspeed/payload/contributors/prices.ts#pricesContributor'],
  },
  {
    field: 'inventory',
    onCreate: 'startingCount',
    modes: all('untouched'),
    whenRemoved: 'left',
    noteKey: 'lightspeedInventory',
    refs: [`${LIGHTSPEED}#syncOneProduct`, `${PARITY}#lightspeedStartingInventory`],
  },
  {
    field: 'barcode',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    whenRemoved: 'left',
    refs: ['src/services/lightspeed/payload/contributors/core.ts#coreContributor', `${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'sku',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    whenRemoved: 'left',
    refs: ['src/services/lightspeed/payload/contributors/core.ts#coreContributor', `${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'weight',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'replace' },
    overrideField: 'weight',
    whenRemoved: 'left',
    noteKey: 'lightspeedWeight',
    refs: ['src/services/lightspeed/payload/contributors/dimensions.ts#dimensionsContributor'],
  },
  {
    field: 'tags',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'add', override: 'add' },
    overrideField: 'tags',
    withoutParity: 'untouched',
    whenRemoved: 'left',
    noteKey: 'lightspeedTags',
    refs: ['src/utils/lightspeedTags.ts#mergeLightspeedTags', `${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'productType',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    overrideField: 'productType',
    whenRemoved: 'left',
    noteKey: 'lightspeedCategory',
    refs: [`${LIGHTSPEED}#syncOneProduct`, `${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'vendor',
    onCreate: true,
    modes: { link_only: 'untouched', enrich: 'fill_blank', override: 'replace' },
    overrideField: 'vendorStatus',
    whenRemoved: 'left',
    refs: [`${LIGHTSPEED}#findOrCreateManufacturer`, `${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'collections',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    refs: [],
  },
  {
    field: 'metafields',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    noteKey: 'lightspeedCustomFields',
    refs: [`${PARITY}#planLightspeedWrite`],
  },
  {
    field: 'seoTitle',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    refs: [],
  },
  {
    field: 'seoDescription',
    onCreate: false,
    modes: all('untouched'),
    whenRemoved: 'left',
    refs: [],
  },
  {
    field: 'status',
    onCreate: true,
    // Tax, discountable, serialized, publish to eCom and item type: set when Motzu creates the item,
    // never resent on an update, with or without catalog parity (MOT-283 #14).
    modes: { link_only: 'untouched', enrich: 'untouched', override: 'untouched' },
    whenRemoved: 'left',
    noteKey: 'lightspeedStatus',
    refs: [`${PARITY}#LIGHTSPEED_CREATE_ONLY_FIELDS`, `${LIGHTSPEED}#withoutCreateOnlyFields`, 'src/services/lightspeed/payload/contributors/core.ts#coreContributor'],
  },
];

/**
 * Lightspeed eCom (C-Series): content only, linked products only. Lightspeed's own Retail → eCom
 * connector owns title, price, stock, brand, photos and category, so Motzu never sends them.
 */
const LIGHTSPEED_ECOM_FIELDS: SyncFieldCell[] = SYNC_BEHAVIOR_FIELDS.map((field): SyncFieldCell => {
  const ecomRefs = ['src/services/lightspeedEcomService.ts#pushContent', 'src/types/lightspeedEcom.ts#LIGHTSPEED_ECOM_FORBIDDEN_FIELDS'];
  if (field === 'description') {
    return {
      field,
      onCreate: false,
      modes: all('replace'),
      ecomSettings: ['content', 'description'],
      whenRemoved: 'left',
      noteKey: 'ecomDescription',
      refs: ecomRefs,
    };
  }
  if (field === 'status') {
    return {
      field,
      onCreate: false,
      modes: all('replace'),
      ecomSettings: ['visibility'],
      whenRemoved: 'left',
      noteKey: 'ecomVisibility',
      refs: ['src/services/lightspeedEcom/contentPayload.ts#resolveLightspeedEcomVisibility'],
    };
  }
  if (field === 'tags') {
    return {
      field,
      onCreate: false,
      modes: all('add'),
      ecomSettings: ['tags'],
      whenRemoved: 'cleared',
      addReconciles: true,
      noteKey: 'ecomTags',
      refs: ['src/services/lightspeedEcomService.ts#syncTags'],
    };
  }
  if (field === 'metafields') {
    return {
      field,
      onCreate: false,
      modes: all('replace'),
      ecomSettings: ['metafields'],
      whenRemoved: 'cleared',
      noteKey: 'ecomMetafields',
      refs: ['src/services/lightspeedEcomService.ts#syncMetafields'],
    };
  }
  return { field, onCreate: false, modes: all('untouched'), whenRemoved: 'left', refs: ecomRefs };
});

const DISCONNECT_REFS = ["src/routes/userIntegrations.ts#router.delete('/:id'"];
/** Disconnect takes down the automatic / register discounts Motzu recorded for the integration (MOT-283 #12). */
const DISCONNECT_CLEANUP = 'src/services/integrationDisconnectCleanupService.ts#removeMotzuDiscountsOnDisconnect';
const LIFECYCLE = 'src/services/shopifyUnsyncLifecycleService.ts';

/**
 * BigCommerce and WooCommerce do not sync yet (MOT-283 surprises #15-#17). Their old whole-catalog
 * push ignored the sync selection and wrote placeholder stock, weight and price, so every write to
 * either platform is refused: the scheduler skips them, Sync now answers 409, and price activation,
 * the midnight batches, the pre-order flip, ERP resync, `/bigcommerce-push` and the compare script
 * refuse too, with `BigCommerceService` refusing any non-GET request as the backstop. The rebuild
 * (selection-based push, MOT-283 Wave 4) brings real rows back here; until then every row is
 * "never sent" and nothing is created.
 */
const SYNC_PAUSED_REFS = [
  `${LEGACY}#refuseWholeCatalogSync`,
  'src/functions/erpSyncScheduler.ts#isSyncPausedPlatform',
  'src/utils/syncPause.ts#assertStoreWritesAllowed',
];
const syncPausedFields = (): SyncFieldCell[] =>
  SYNC_BEHAVIOR_FIELDS.map((field): SyncFieldCell => ({ field, onCreate: false, modes: all('untouched'), whenRemoved: 'left', refs: SYNC_PAUSED_REFS }));
const syncPausedEvents = (): SyncEventCell[] => [
  { event: 'deselect', outcome: 'syncPaused', refs: SYNC_PAUSED_REFS },
  { event: 'modeChange', outcome: 'noModes', refs: [] },
  { event: 'overrideOff', outcome: 'syncPaused', refs: SYNC_PAUSED_REFS },
  { event: 'disconnect', outcome: 'leftInStore', refs: DISCONNECT_REFS },
  { event: 'scheduled', outcome: 'syncPausedScheduled', refs: SYNC_PAUSED_REFS },
  { event: 'deletedInStore', outcome: 'syncPaused', refs: SYNC_PAUSED_REFS },
];

export const SYNC_BEHAVIOR: Record<SyncBehaviorPlatform, SyncPlatformBehavior> = {
  shopify: {
    platform: 'shopify',
    honoursSyncMode: true,
    honoursOverrideFields: true,
    fields: SHOPIFY_FIELDS,
    events: [
      { event: 'deselect', outcome: 'shopifyDeselect', refs: ['src/services/shopifyListingShapeService.ts#markRemovedFromSelection', 'src/utils/shopifyListingShape.ts#DEFAULT_LISTING_SHAPE', `${LIFECYCLE}#runDeselectedPass`] },
      { event: 'modeChange', outcome: 'nothingUndone', refs: [`${SHOPIFY}#syncOneProductToShopifyInternal`] },
      { event: 'overrideOff', outcome: 'keptAsIs', refs: [`${SHOPIFY}#updateShopifyProduct`] },
      { event: 'disconnect', outcome: 'shopifyDisconnect', refs: [...DISCONNECT_REFS, DISCONNECT_CLEANUP] },
      { event: 'scheduled', outcome: 'shopifyScheduled', refs: ['src/functions/erpSyncScheduler.ts#runScheduledErpSync'] },
      { event: 'deletedInStore', outcome: 'shopifyDeleted', refs: [`${SHOPIFY}#syncOneProductToShopifyInternal`, `${LIFECYCLE}#holdDeletedListing`] },
    ],
  },
  lightspeed: {
    platform: 'lightspeed',
    honoursSyncMode: true,
    honoursOverrideFields: true,
    fields: LIGHTSPEED_FIELDS,
    events: [
      { event: 'deselect', outcome: 'lightspeedDeselect', refs: [`${PARITY}#LIGHTSPEED_CATALOG_PARITY_FLAG`] },
      { event: 'modeChange', outcome: 'nothingUndone', refs: [`${PARITY}#planLightspeedWrite`] },
      { event: 'overrideOff', outcome: 'keptAsIs', refs: [`${PARITY}#planLightspeedWrite`] },
      { event: 'disconnect', outcome: 'lightspeedDisconnect', refs: [...DISCONNECT_REFS, DISCONNECT_CLEANUP, 'src/services/lightspeedSalesService.ts#archiveAllRecordedDiscounts'] },
      { event: 'scheduled', outcome: 'fullEachRun', refs: [`${LEGACY}#syncLightspeed`] },
      { event: 'deletedInStore', outcome: 'lightspeedDeleted', refs: [`${LIGHTSPEED}#syncOneProduct`] },
    ],
  },
  lightspeed_ecom: {
    platform: 'lightspeed_ecom',
    honoursSyncMode: false,
    honoursOverrideFields: false,
    fields: LIGHTSPEED_ECOM_FIELDS,
    events: [
      { event: 'deselect', outcome: 'leftInStore', refs: ['src/services/lightspeedEcomService.ts#pushContent'] },
      { event: 'modeChange', outcome: 'noModes', refs: [] },
      { event: 'overrideOff', outcome: 'ecomFieldOff', refs: ['src/types/lightspeedEcom.ts#LIGHTSPEED_ECOM_PUSH_DEFAULTS'] },
      { event: 'disconnect', outcome: 'leftInStore', refs: DISCONNECT_REFS },
      { event: 'scheduled', outcome: 'fullEachRun', refs: [`${LEGACY}#syncLightspeedEcom`] },
      { event: 'deletedInStore', outcome: 'neverCreated', refs: ['src/services/lightspeedEcomService.ts#pushContent'] },
    ],
  },
  bigcommerce: {
    platform: 'bigcommerce',
    honoursSyncMode: false,
    honoursOverrideFields: false,
    syncPaused: true,
    fields: syncPausedFields(),
    events: syncPausedEvents(),
  },
  woocommerce: {
    platform: 'woocommerce',
    honoursSyncMode: false,
    honoursOverrideFields: false,
    syncPaused: true,
    fields: syncPausedFields(),
    events: syncPausedEvents(),
  },
};

export const isSyncBehaviorPlatform = (platform: string | null | undefined): platform is SyncBehaviorPlatform =>
  Boolean(platform) && Object.prototype.hasOwnProperty.call(SYNC_BEHAVIOR, platform as string);

/** Feature flags (for the integration's organization) that change what a push writes. */
export interface SyncBehaviorFlags {
  lightspeedCatalogParity?: boolean;
  lightspeedLinkOnly?: boolean;
  /** `lightspeed_store_config`: the Override Title and Description choices keep the item name and web copy. */
  lightspeedStoreConfig?: boolean;
  /** `initial_sync_mode`: `unmatchedProductAction` and the starting count on create apply. */
  initialSyncMode?: boolean;
  /** `shopify_listing_shape`: `onUnsync` applies. */
  shopifyListingShape?: boolean;
  /** `shopify_metafield_switches`: `motzuMetafields` applies. */
  shopifyMetafieldSwitches?: boolean;
  /** `shopify_owned_field_cleanup`: Motzu takes back the tags and metafields it added and no longer sends. */
  shopifyOwnedFieldCleanup?: boolean;
  /** `shopify_unsync_cleanup`: a removed product loses the compare-at and rebate field Motzu set. */
  shopifyUnsyncCleanup?: boolean;
  /** `shopify_respect_store_deletes`: a listing deleted in Shopify stays deleted unless `recreateDeletedProducts`. */
  shopifyRespectStoreDeletes?: boolean;
  /** `enrich_fill_only`: Fill gaps only fills blanks (Shopify category, Lightspeed web copy). */
  enrichFillOnly?: boolean;
  /** `lightspeed_photo_topup`: an item with fewer photos than Motzu sends gets Motzu's set. */
  lightspeedPhotoTopUp?: boolean;
}

/** The note under a field for these flags: the Fill-gaps-fills-blanks-only copy while `enrich_fill_only` is on. */
export function fieldNoteKey(cell: SyncFieldCell, flags: SyncBehaviorFlags = {}): string | undefined {
  if (flags.lightspeedPhotoTopUp && cell.photoTopUpNoteKey) return cell.photoTopUpNoteKey;
  return flags.enrichFillOnly && cell.fillOnlyNoteKey ? cell.fillOnlyNoteKey : cell.noteKey;
}

/**
 * The mode the push runs for this integration. A missing mode is Override (the push's own
 * default). On Lightspeed, Link only needs `lightspeed_link_only` and Fill gaps needs
 * `lightspeed_catalog_parity`; without them the push writes the full item, as Override does.
 * Platforms with no modes report Override, which is the column their cells fill.
 */
export function resolveSyncMode(
  platform: SyncBehaviorPlatform,
  settings: Pick<SyncBehaviorSettings, 'syncMode'> | null | undefined,
  flags: SyncBehaviorFlags = {},
): SyncMode {
  if (!SYNC_BEHAVIOR[platform].honoursSyncMode) return 'override';
  const mode: SyncMode = settings?.syncMode ?? 'override';
  if (platform === 'lightspeed') {
    if (mode === 'link_only' && !flags.lightspeedLinkOnly) return 'override';
    if (mode === 'enrich' && !flags.lightspeedCatalogParity) return 'override';
  }
  return mode;
}

/**
 * What one sync of this integration does to a field right now.
 * - `off`: a Data sync switch (or, on Lightspeed eCom, a "what to push" switch) is off.
 * - `switched_off`: the store turned this Motzu field off.
 * - `kept`: the store keeps its own value under "What Override replaces" (or Lightspeed's fields you keep).
 */
export type EffectiveFieldWrite = SyncFieldWrite | 'off' | 'switched_off' | 'kept';

/** Whether an Override choice keeps the store's value for this field, as the platform's push reads it. */
function overrideChoiceKeeps(
  platform: SyncBehaviorPlatform,
  cell: SyncFieldCell,
  mode: SyncMode,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags,
): boolean {
  const key = cell.overrideField;
  if (!key || settings?.overrideFields?.[key] !== false) return false;
  if (platform === 'shopify') return mode === 'override';
  if (platform === 'lightspeed') {
    if (mode === 'link_only') return false;
    // `lightspeed_store_config` keeps the item name and web copy in every mode (`storeConfig.keep`).
    if (flags.lightspeedStoreConfig && (key === 'title' || key === 'description')) return true;
    // `planLightspeedWrite` reads the choices in Override; its Description choice governs the item
    // note only, not the web description this row is about.
    return flags.lightspeedCatalogParity === true && mode === 'override' && key !== 'description';
  }
  return false;
}

export function effectiveFieldWrite(
  platform: SyncBehaviorPlatform,
  cell: SyncFieldCell,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags = {},
): EffectiveFieldWrite {
  const mode = resolveSyncMode(platform, settings, flags);
  const parityOff = platform === 'lightspeed' && flags.lightspeedCatalogParity !== true;
  const base = parityOff && mode !== 'link_only' && cell.withoutParity
    ? cell.withoutParity
    : mode === 'enrich' && flags.enrichFillOnly && cell.enrichFillOnly ? cell.enrichFillOnly : cell.modes[mode];
  if (base === 'untouched') return 'untouched';
  if (cell.toggle && settings?.[cell.toggle] === false) return 'off';
  const modeToggle = cell.modeToggles?.[mode];
  if (modeToggle && settings?.[modeToggle] === false) return 'off';
  if (cell.ecomSettings) {
    const on = cell.ecomSettings.some((key) => settings?.lightspeedEcom?.[key] ?? LIGHTSPEED_ECOM_PUSH_DEFAULTS_MIRROR[key]);
    if (!on) return 'off';
  }
  if (
    cell.motzuMetafieldKey
    && flags.shopifyMetafieldSwitches
    && settings?.motzuMetafields?.[cell.motzuMetafieldKey] === false
  ) {
    return 'switched_off';
  }
  // Lightspeed link only reprices only while `lightspeedLinkOnlyPrices` says so.
  if (platform === 'lightspeed' && mode === 'link_only' && cell.field === 'price') {
    const reprices = settings?.linkOnlyPricing === true || (flags.lightspeedCatalogParity === true && settings?.linkOnlyPricing !== false);
    if (!reprices) return 'off';
  }
  if (overrideChoiceKeeps(platform, cell, mode, settings, flags)) return 'kept';
  return base;
}

/** Whether a sync of this integration creates a product that has no listing in the store yet. */
export function createsProducts(
  platform: SyncBehaviorPlatform,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags = {},
): boolean {
  if (platform === 'lightspeed_ecom') return false;
  if (SYNC_BEHAVIOR[platform].syncPaused) return false;
  const mode = resolveSyncMode(platform, settings, flags);
  if (platform === 'lightspeed') return mode !== 'link_only';
  // Shopify: `syncOneProductToShopifyInternal` with `resolveInitialSyncOptions`.
  if (settings?.syncProducts === false) return false;
  if (!flags.initialSyncMode) return mode !== 'link_only';
  if (settings?.unmatchedProductAction === 'skip') return false;
  if (settings?.unmatchedProductAction === 'create') return true;
  return mode !== 'link_only';
}

/** What a product Motzu creates gets for this field: `yes`, `no`, or `none` when nothing is created. */
export function createOutcome(
  platform: SyncBehaviorPlatform,
  cell: SyncFieldCell,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags = {},
): 'yes' | 'no' | 'none' {
  if (!createsProducts(platform, settings, flags)) return 'none';
  const rule = cell.onCreate;
  if (rule === true) return 'yes';
  if (rule === false) return 'no';
  if (rule === 'syncImages' || rule === 'syncPricing') return settings?.[rule] === false ? 'no' : 'yes';
  // `startingCount`: a count must be saved, and the push sets it only with stock sync on
  // (Shopify also with `initial_sync_mode`'s seeding; Lightspeed only with the parity flag).
  const count = settings?.defaultInventoryCount;
  if (typeof count !== 'number' || !Number.isFinite(count)) return 'no';
  if (platform === 'shopify') return settings?.syncInventory === true || flags.initialSyncMode === true ? 'yes' : 'no';
  if (platform === 'lightspeed') return settings?.syncInventory === true && flags.lightspeedCatalogParity === true ? 'yes' : 'no';
  return 'yes';
}

/** The outcome copy key for an event on this integration, from its saved settings and flags. */
export function eventOutcome(
  platform: SyncBehaviorPlatform,
  event: SyncEventCell,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags = {},
): string {
  if (platform === 'shopify' && event.event === 'deselect') {
    // `runDeselectedPass` clears the deal only while Motzu writes prices.
    const clears = flags.shopifyUnsyncCleanup === true && settings?.syncPricing !== false;
    const unsync = flags.shopifyListingShape ? settings?.onUnsync : undefined;
    if (unsync === 'draft') return clears ? 'shopifyDeselectDraftDealCleared' : 'shopifyDeselectDraft';
    if (unsync === 'archive') return clears ? 'shopifyDeselectArchiveDealCleared' : 'shopifyDeselectArchive';
    if (clears) return 'shopifyDeselectDealCleared';
  }
  if (platform === 'shopify' && event.event === 'deletedInStore') {
    const mode = resolveSyncMode(platform, settings, flags);
    if (mode === 'link_only') return 'shopifyDeletedLinkOnly';
    if (!createsProducts(platform, settings, flags)) return 'shopifyDeletedSkipped';
    if (flags.shopifyRespectStoreDeletes && settings?.recreateDeletedProducts !== true) return 'shopifyDeletedKept';
  }
  if (platform === 'lightspeed' && event.event === 'deletedInStore' && resolveSyncMode(platform, settings, flags) === 'link_only') {
    return 'neverCreated';
  }
  return event.outcome;
}

/**
 * Whether this integration's push takes back what it added to this field and no longer sends (flag
 * `shopify_owned_field_cleanup`, in the cell's `ownedCleanup` modes). Tags in the `marker_only`
 * tag mode stay the store's. The write itself must still happen: a field kept or off is untouched.
 */
export function reconcilesOwned(
  platform: SyncBehaviorPlatform,
  cell: SyncFieldCell,
  settings: SyncBehaviorSettings | null | undefined,
  flags: SyncBehaviorFlags = {},
): boolean {
  if (platform !== 'shopify' || !flags.shopifyOwnedFieldCleanup || !cell.ownedCleanup) return false;
  if (!cell.ownedCleanup.includes(resolveSyncMode(platform, settings, flags))) return false;
  if (cell.field === 'tags' && settings?.tagMode === 'marker_only') return false;
  return true;
}

const NOT_WRITTEN: ReadonlySet<EffectiveFieldWrite> = new Set<EffectiveFieldWrite>(['untouched', 'off', 'switched_off', 'kept']);

/**
 * What happens to the store's value when Motzu's value changes, for a field written as `write`.
 * `reconciles` (from `reconcilesOwned`) says Motzu takes back what it added; omitted = the cell's own.
 */
export function changeOutcome(
  cell: SyncFieldCell,
  write: EffectiveFieldWrite,
  reconciles = cell.addReconciles === true,
): 'overwritten' | 'added' | 'notResent' | 'untouched' {
  if (NOT_WRITTEN.has(write)) return 'untouched';
  if (write === 'fill_blank') return 'notResent';
  if (write === 'add') return reconciles ? 'overwritten' : 'added';
  return 'overwritten';
}

/** What happens to the store's value when Motzu no longer has one, for a field written as `write`. */
export function removalOutcome(
  cell: SyncFieldCell,
  write: EffectiveFieldWrite,
  reconciles = cell.addReconciles === true,
): 'cleared' | 'left' | 'untouched' {
  if (NOT_WRITTEN.has(write)) return 'untouched';
  if (write === 'fill_blank') return 'left';
  if (write === 'add') return reconciles ? 'cleared' : 'left';
  if (reconciles) return 'cleared';
  return cell.whenRemoved;
}
